import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Droplets, Loader2, Camera, Home as HomeIcon, Crown, MapPin, ExternalLink, ImageOff } from 'lucide-react';
import PageLayout from '../components/layout/PageLayout';
import { api, mediaUrl } from '../services/api';

const NAV_LINKS = [
  { label: 'Sobre Nós', to: '/sobre' },
  { label: 'Pregações', to: '/pregações' },
  { label: 'Células', to: '/celulas' },
  { label: 'Eventos', to: '/evento' },
  { label: 'Galeria', to: '/galeria' },
  { label: 'Contato', to: '/contato' },
];

const FOOTER_COLUMNS = [
  {
    title: 'Navegação',
    links: [
      ['Em que Cremos', '/sobre'],
      ['História', '/sobre'],
      ['Eventos', '/evento'],
      ['Células', '/celulas'],
      ['Galeria', '/galeria'],
    ],
  },
  {
    title: 'Institucional',
    links: [
      ['Política de Privacidade', '/privacidade'],
      ['Relatórios', '/relatorios'],
      ['Doações', '/contato'],
      ['Área Administrativa', '/admin'],
    ],
  },
];

function PhotosSection({ testid, icon, title, photos = [], caption = '', cols = 'md:grid-cols-3', emptyText = 'Nenhuma foto enviada ainda' }) {
  const hasCaption = photos.length === 1 && caption;
  return (
    <section className="mt-6" data-testid={testid}>
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="font-heading text-lg font-bold text-ink md:text-xl">{title}</h2>
      </div>
      {photos.length === 0 ? (
        <div className="mt-3 rounded-2xl border border-dashed border-slate-300 p-8 text-center">
          <ImageOff size={26} className="mx-auto text-ink-faint" />
          <p className="mt-2 font-heading font-semibold text-ink">{emptyText}</p>
        </div>
      ) : (
        <div className={`mt-3 grid grid-cols-2 gap-3 ${cols}`}>
          {photos.map((src, i) => (
            <figure key={src} className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-soft">
              <img
                src={mediaUrl(src)}
                alt={caption ? `${caption} — foto ${i + 1}` : `Foto ${i + 1}`}
                loading="lazy"
                className="aspect-square w-full object-cover"
              />
              {hasCaption && <figcaption className="px-3 py-2 text-center text-sm font-semibold text-ink">{caption}</figcaption>}
            </figure>
          ))}
        </div>
      )}
    </section>
  );
}

export default function CellPhotos() {

  const { id } = useParams();
  const [cell, setCell] = useState(null);
  const [network, setNetwork] = useState(null);
  const [loading, setLoading] = useState(true);

  // Busca a célula diretamente no backend (GET /cells/:id). As fotos exibidas
  // são os uploads gravados em leaderPhotos/hostPhotos/meetingPhotos.
  useEffect(() => {
    let active = true;
    Promise.all([api.getCell(id), api.getNetworks()])
      .then(([c, networks]) => {
        if (!active) return;
        setCell(c || null);
        setNetwork(c ? networks.find((n) => n.id === c.networkId) : null);
      })
      .catch(() => { if (active) setCell(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [id]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 size={26} className="animate-spin text-primary" />
      </div>
    );
  }

  if (!cell) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
        <Camera size={30} className="text-ink-faint" />
        <p className="mt-3 font-heading text-lg font-bold text-ink">Célula não encontrada</p>
        <Link to="/celulas" className="mt-4">
          <span className="rounded-full bg-primary px-5 py-2.5 font-heading font-semibold text-white">Ver todas as células</span>
        </Link>
      </div>
    );
  }

  // Fotos gravadas no backend via admin (uploads em /uploads/cells/...).
  const leaderPhotos = cell.leaderPhotos || [];
  const hostPhotos = cell.hostPhotos || [];
  const meetingPhotos = cell.meetingPhotos || [];

  const addr = cell.address;
  const addressLine = `${addr.street}, ${addr.number} — ${addr.neighborhood}, ${addr.city}`;
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressLine)}`;

  return (
    <PageLayout navLinks={NAV_LINKS} footerColumns={FOOTER_COLUMNS}>
      <div data-testid="cell-photos-page">
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-primary to-primary-dark px-5 pb-6 pt-5 text-white md:px-8">
        <div className="app-container !px-0">
          <div className="flex items-center justify-between">
            <Link to={`/celula/${cell.id}`} className="flex items-center gap-1.5 text-sm font-semibold text-white/90 hover:text-white" data-testid="photos-back">
              <ArrowLeft size={16} /> Célula
            </Link>
            <div className="flex items-center gap-1.5 text-sm font-semibold text-white/90">
              <Droplets size={16} /> Águas Vivas
            </div>
          </div>
          <div className="mt-6">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent-light">Fotos da célula</span>
            <h1 className="mt-1 font-heading text-2xl font-extrabold leading-tight md:text-3xl">{cell.name}</h1>
            <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-sm font-semibold backdrop-blur">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: network?.color || '#f59e0b' }} />
              {network?.name}
            </span>
          </div>
        </div>
      </div>

      <div className="app-container py-5">

        {/* Líder — uploads do backend (leaderPhotos) */}
        <PhotosSection
          testid="photos-leader"
          icon={<Crown size={18} className="text-primary" />}
          title="Líder"
          photos={leaderPhotos}
          caption={cell.leader?.name || ''}
        />

        {/* Anfitrião — uploads do backend (hostPhotos) */}
        <PhotosSection
          testid="photos-host"
          icon={<HomeIcon size={18} className="text-accent-dark" />}
          title="Anfitrião"
          photos={hostPhotos}
          caption={cell.host?.name || ''}
        />

        {/* Fotos das reuniões — uploads do backend (meetingPhotos) */}
        <PhotosSection
          testid="photos-gallery"
          icon={<Camera size={18} className="text-emerald-600" />}
          title="Fotos das reuniões"
          photos={meetingPhotos}
          cols="md:grid-cols-3 xl:grid-cols-5"
          emptyText="Ainda não há fotos das reuniões"
        />

        {/* Endereço e mapa */}
        <section className="mt-6" data-testid="photos-address">
          <div className="flex items-center gap-2">
            <MapPin size={18} className="text-rose-600" />
            <h2 className="font-heading text-lg font-bold text-ink md:text-xl">Endereço</h2>
          </div>
          <div className="mt-3 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-soft sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-600">
                <MapPin size={20} />
              </span>
              <div>
                <p className="font-heading font-semibold text-ink">{addressLine}</p>
                <p className="mt-0.5 text-sm text-ink-soft">
                  {cell.day} às {cell.time}
                </p>
              </div>
            </div>
            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="photos-map-link"
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-primary px-5 py-2.5 font-heading font-semibold text-white shadow-soft transition-transform duration-200 hover:-translate-y-0.5"
            >
              <ExternalLink size={16} /> Ver no mapa
            </a>
          </div>
        </section>

        <div className="mt-8 flex justify-center">
          <Link to={`/celula/${cell.id}`} className="rounded-full bg-primary px-6 py-3 font-heading font-semibold text-white shadow-soft transition-transform duration-200 hover:-translate-y-0.5">
            Voltar para a célula
          </Link>
        </div>
      </div>
      </div>
    </PageLayout>
  );
}


